import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import ModuleShell from '../../components/ModuleShell';
import {
  ADMIN_IHVG_APPROVE_PACK,
  ADMIN_IHVG_AUDIO_BEDS,
  ADMIN_IHVG_CAPTION_IDEAS,
  ADMIN_IHVG_CLIP_PRESETS,
  ADMIN_IHVG_ENSURE_DEFAULTS,
  ADMIN_IHVG_GENERATE_PACK,
  ADMIN_IHVG_IMPORT_ASSET,
  ADMIN_IHVG_PACK,
  ADMIN_IHVG_PACKS,
  ADMIN_IHVG_PROOF_CATALOG,
  ADMIN_IHVG_RETRY_PUBLISH,
  ADMIN_IHVG_SCHEDULE_PACK,
  ADMIN_IHVG_STOCK_CATALOG,
  ADMIN_IHVG_UPDATE_PACK_DRAFT,
} from '../../graphql/adminQueries';
import { useAuth } from '../../auth/AuthContext';
import { formatDateTime } from '../../utils/format';

export const IHVG_PLATFORMS = [
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'linkedin',
];

export const VIDEO_SECTIONS = [
  { id: 'review', label: 'Review' },
  { id: 'create', label: 'New' },
  { id: 'import', label: 'Import' },
];

/** Summarize ensure/compose/refill mutation for the status line. */
export function ensureIdeasMessage(result) {
  const payload = result?.data?.adminIhvgEnsureDefaults || result || {};
  const created = payload.created?.length || 0;
  const composed = payload.composed?.length || 0;
  const refilled = payload.refilled?.length || 0;
  if (refilled) return `Generated ${refilled} new idea(s) for review`;
  if (composed && created) return `Seeded ${created}, composed ${composed} for review`;
  if (composed) return `Composed ${composed} idea(s) for review`;
  if (created) return `Seeded ${created} idea(s)`;
  return 'Ideas up to date';
}

export function packsNeedCompose(packs) {
  return (packs || []).some(
    (pack) => !pack.mediaSha256
      && (pack.status === 'draft' || pack.status === 'failed' || pack.status === 'composing'),
  );
}

export const SANDBOX_SCHEDULE_NOTE =
  'Schedules go through website-api → RobinReach. Default publish mode is sandbox (no live social blast). First live prod schedule needs founder OK + runtime secrets.';

/** Cold default CTA — demand brief: fork → Basic yearly (not Free-only). */
export const DEFAULT_COLD_CTA =
  'https://fiscalflowcast.com/signup?plan=basic&billing=yearly';

export const DEFAULT_COLD_CTA_LABEL = 'Start Basic yearly';

/** Operator Review rule from demand-brief channel updates. */
export const REVIEW_POSITIONING_HINT =
  'Approve packs that name a fork + consequence + quiet Basic yearly ask. '
  + 'Reject budgeting-app identity, calculator tourism, Monte Carlo jargon, '
  + 'Free-only CTAs, wizard landers, cold /pricing-only, and cold Premium unlock. '
  + 'Rotate housing, career, move-abroad, and cash-forward — not housing-only.';

export const AUDIO_MODES = [
  { id: 'silent', label: 'Silent + captions (default)' },
  { id: 'royalty_free_bed', label: 'Royalty-free bed (licensed import only)' },
];

export const PLATFORM_SOUND_CHECKLIST =
  'RobinReach schedule is caption + media only today. After schedule (or in native UI), attach platform-licensed sounds if desired: TikTok CML / music_sound_id, IG audio_id, FB Sound Collection, YouTube Shorts/Audio Library. Do not mux Drake/Spotify/commercial rips into the master.';

/** Mirrors website-api OVERLAY_FONT_ALLOWLIST — OFL faces only. */
export const OVERLAY_FONT_OPTIONS = [
  { id: 'montserrat_extrabold', label: 'Montserrat ExtraBold', cssFamily: 'Montserrat ExtraBold', weight: 800 },
  { id: 'montserrat_bold', label: 'Montserrat Bold', cssFamily: 'Montserrat Bold', weight: 700 },
  { id: 'montserrat_black', label: 'Montserrat Black', cssFamily: 'Montserrat Black', weight: 900 },
  { id: 'oswald_bold', label: 'Oswald Bold', cssFamily: 'Oswald Bold', weight: 700 },
  { id: 'anton', label: 'Anton', cssFamily: 'Anton', weight: 400 },
];

export const DEFAULT_OVERLAY_STYLE = {
  fontId: 'montserrat_extrabold',
  fillColor: '#FFFFFF',
  outlineColor: '#000000',
};

export function normalizeOverlayStyleForm(input) {
  const raw = input && typeof input === 'object' ? input : {};
  const fontId = OVERLAY_FONT_OPTIONS.some((f) => f.id === raw.fontId)
    ? raw.fontId
    : DEFAULT_OVERLAY_STYLE.fontId;
  const hex = (value, fallback) => {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(String(value || '').trim());
    return m ? `#${m[1].toUpperCase()}` : fallback;
  };
  return {
    fontId,
    fillColor: hex(raw.fillColor, DEFAULT_OVERLAY_STYLE.fillColor),
    outlineColor: hex(raw.outlineColor, DEFAULT_OVERLAY_STYLE.outlineColor),
  };
}

export function overlayStyleCssVars(style) {
  const normalized = normalizeOverlayStyleForm(style);
  const font = OVERLAY_FONT_OPTIONS.find((f) => f.id === normalized.fontId)
    || OVERLAY_FONT_OPTIONS[0];
  return {
    '--ihvg-overlay-fill': normalized.fillColor,
    '--ihvg-overlay-outline': normalized.outlineColor,
    '--ihvg-overlay-font': `'${font.cssFamily}', system-ui, sans-serif`,
    '--ihvg-overlay-weight': String(font.weight),
  };
}

/** Parse publishStatus JSON into per-platform rows for the status table. */
export function platformStatusRows(publishStatus) {
  if (!publishStatus || typeof publishStatus !== 'object') return [];
  const platforms = publishStatus.platforms;
  if (platforms && typeof platforms === 'object' && !Array.isArray(platforms)) {
    return Object.entries(platforms).map(([platform, row]) => ({
      platform,
      status: row?.status || row?.state || 'unknown',
      externalId: row?.externalId || row?.postId || row?.id || null,
      error: row?.error || row?.message || null,
    }));
  }
  if (Array.isArray(platforms)) {
    return platforms.map((row) => ({
      platform: row?.platform || 'unknown',
      status: row?.status || row?.state || 'unknown',
      externalId: row?.externalId || row?.postId || row?.id || null,
      error: row?.error || row?.message || null,
    }));
  }
  return [];
}

export function canApprove(pack) {
  if (!pack) return false;
  if (pack.status === 'ready') return true;
  return pack.status === 'draft' && Boolean(pack.mediaSha256);
}

export function canSchedule(pack) {
  if (!pack) return false;
  if (pack.status === 'approved') return true;
  // Mid-flight crash leaves status=scheduling — allow Schedule/Retry recovery.
  if (pack.status === 'scheduling') return true;
  if (pack.status === 'failed' && pack.publishStatus?.platforms) return true;
  return false;
}

/** Packs waiting for reject / edit / approve in the review deck. */
export function isReviewable(pack) {
  if (!pack) return false;
  // Ready (or draft/composing that already has playable media).
  // Bare `composing` without sha must NOT own the queue (zombie after timeout).
  if (pack.status === 'ready') return true;
  if (Boolean(pack.mediaSha256 || pack.previewUrl)) {
    return pack.status === 'draft' || pack.status === 'composing';
  }
  return false;
}

export function reviewQueue(packs, dismissedIds = []) {
  const dismissed = new Set(dismissedIds);
  return (packs || []).filter((pack) => isReviewable(pack) && !dismissed.has(pack.id));
}

export function packFormat(pack) {
  const raw = pack?.contentFormat
    || pack?.publishStatus?.content?.format
    || 'video_reel';
  if (raw === 'image_post' || raw === 'text_post' || raw === 'video_reel') return raw;
  return 'video_reel';
}

export function packFormatLabel(format) {
  if (format === 'image_post') return 'Image';
  if (format === 'text_post') return 'Text';
  return 'Video';
}

export function packFamily(pack) {
  return pack?.contentFamily
    || pack?.publishStatus?.content?.family
    || pack?.publishStatus?.content?.angle
    || pack?.slug
    || 'idea';
}

/** Family + format so video/image/text options never share one chip row. */
export function packFamilyKey(pack) {
  return `${packFormat(pack)}:${packFamily(pack)}`;
}

export function packVariantLabel(pack) {
  return pack?.contentVariantLabel
    || pack?.publishStatus?.content?.variantLabel
    || pack?.contentVariant
    || pack?.publishStatus?.content?.variant
    || 'Option';
}

/** Platforms allowed for schedule by content format (matches website-api). */
export function platformsForFormat(format) {
  if (format === 'image_post') return ['instagram', 'facebook', 'linkedin', 'tiktok'];
  if (format === 'text_post') return ['facebook', 'linkedin'];
  return ['instagram', 'facebook', 'tiktok', 'youtube', 'linkedin'];
}

export function defaultPlatformsForFormat(format) {
  const allowed = platformsForFormat(format);
  if (format === 'text_post') return ['facebook'];
  if (allowed.includes('instagram')) return ['instagram'];
  return [allowed[0]];
}

export function distinctFormats(packs, dismissedIds = []) {
  const set = new Set(reviewQueue(packs, dismissedIds).map((p) => packFormat(p)));
  return [...set];
}

/** Sibling options for the same idea family+format (multi-option Review). */
export function variantOptions(packs, familyKey, dismissedIds = []) {
  const dismissed = new Set(dismissedIds);
  return (packs || []).filter(
    (pack) => packFamilyKey(pack) === familyKey
      && isReviewable(pack)
      && !dismissed.has(pack.id),
  );
}

/** One representative pack per family+format for the Review deck progress. */
export function reviewFamilies(packs, dismissedIds = []) {
  const queue = reviewQueue(packs, dismissedIds);
  const seen = new Set();
  const families = [];
  for (const pack of queue) {
    const key = packFamilyKey(pack);
    if (seen.has(key)) continue;
    seen.add(key);
    families.push(key);
  }
  return families;
}

export function filterPacksByFormat(packs, formatFilter) {
  if (!formatFilter || formatFilter === 'all') return packs || [];
  return (packs || []).filter((p) => packFormat(p) === formatFilter);
}

export function nextReviewId(queue, currentId) {
  if (!queue.length) return null;
  const idx = queue.findIndex((p) => p.id === currentId);
  if (idx < 0) return queue[0].id;
  if (idx + 1 < queue.length) return queue[idx + 1].id;
  if (idx - 1 >= 0) return queue[idx - 1].id;
  return null;
}

export function scheduleOutcomeMessage(pack) {
  const rows = platformStatusRows(pack?.publishStatus);
  if (!rows.length) return 'Schedule OK';
  const failed = rows.filter((row) => row.status === 'failed');
  const ok = rows.filter((row) => row.status === 'scheduled');
  if (failed.length && ok.length) {
    return `Partial schedule: ${ok.length} ok, ${failed.length} failed — see platform table`;
  }
  if (failed.length) return `Schedule failed (${failed.length} platform(s))`;
  return 'Schedule OK';
}

export function mutationMessage(error) {
  if (!error) return null;
  return error.graphQLErrors?.[0]?.message || error.message || 'Request failed';
}

/** Operator-facing load banner — distinguish auth vs missing ops vs other GraphQL errors. */
export function ihvgLoadErrorLabel(error) {
  if (!error) return null;
  const code = error.graphQLErrors?.[0]?.extensions?.code;
  const msg = String(error.graphQLErrors?.[0]?.message || error.message || '');
  if (
    code === 'UNAUTHENTICATED'
    || /must be authenticated|session expired|invalid token/i.test(msg)
  ) {
    return 'Session expired — sign in again to load videos.';
  }
  if (/Cannot query field|Unknown type|adminIhvg/i.test(msg)) {
    return 'Could not load video GraphQL. Confirm website-api adminIhvg* ops are deployed.';
  }
  return `Could not load videos: ${msg || 'request failed'}`;
}

export function statusBadgeClass(status) {
  const s = String(status || 'unknown');
  if (['ready', 'approved', 'scheduled'].includes(s)) return `ihvg-status ihvg-status-${s}`;
  if (['failed', 'composing', 'scheduling'].includes(s)) return `ihvg-status ihvg-status-${s}`;
  if (s === 'draft') return 'ihvg-status ihvg-status-draft';
  return 'ihvg-status';
}

export function packCounts(packs) {
  const counts = {
    total: packs.length,
    draft: 0,
    ready: 0,
    approved: 0,
    scheduled: 0,
    failed: 0,
    other: 0,
  };
  for (const pack of packs) {
    if (counts[pack.status] != null) counts[pack.status] += 1;
    else counts.other += 1;
  }
  return counts;
}

export function autoSlugFromCaption(caption, proofId) {
  const base = String(caption || proofId || 'reel')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `${base || 'reel'}-${Date.now().toString(36).slice(-4)}`;
}

/** Filter proof/stock catalog rows by operator search (id, label, surface). */
export function filterCatalogByQuery(rows, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((row) => {
    const hay = `${row.id || ''} ${row.label || ''} ${row.surface || ''} ${row.kind || ''}`.toLowerCase();
    return hay.includes(q);
  });
}

/** Prefer data: for <img> so Review stills paint even if img-src lacks blob: (legacy CSP). */
export async function mediaBlobToPreviewSrc(blob, { preferDataUrl = false } = {}) {
  if (!blob) return null;
  if (!preferDataUrl) return URL.createObjectURL(blob);
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  const mime = blob.type || 'application/octet-stream';
  return `data:${mime};base64,${btoa(binary)}`;
}

function defaultScheduleLocal() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function SectionTabs({ active, onChange }) {
  return (
    <div className="ihvg-tabs" role="tablist" aria-label="Videos sections">
      {VIDEO_SECTIONS.map((section) => (
        <button
          key={section.id}
          type="button"
          role="tab"
          id={`ihvg-tab-${section.id}`}
          aria-selected={active === section.id}
          aria-controls={`ihvg-panel-${section.id}`}
          className={
            active === section.id ? 'ihvg-tab ihvg-tab-active' : 'ihvg-tab'
          }
          onClick={() => onChange(section.id)}
        >
          {section.label}
        </button>
      ))}
    </div>
  );
}

function PackStatusBadge({ status }) {
  return <span className={statusBadgeClass(status)}>{status}</span>;
}

function Disclosure({ summary, children, defaultOpen = false }) {
  return (
    <details className="ihvg-disclosure" open={defaultOpen || undefined}>
      <summary>{summary}</summary>
      <div className="ihvg-disclosure-body">{children}</div>
    </details>
  );
}

export default function VideosModule() {
  const { isAdmin } = useAuth();
  const [section, setSection] = useState('review');
  const [selectedId, setSelectedId] = useState(null);
  const [slug, setSlug] = useState('');
  const [caption, setCaption] = useState('');
  const [cta, setCta] = useState(DEFAULT_COLD_CTA);
  const [selectedProofs, setSelectedProofs] = useState([]);
  const [platforms, setPlatforms] = useState(['instagram']);
  const [scheduledLocal, setScheduledLocal] = useState(defaultScheduleLocal);
  const [actionMessage, setActionMessage] = useState(null);
  const [seedAttempted, setSeedAttempted] = useState(false);
  const [audioMode, setAudioMode] = useState('silent');
  const [bedAssetId, setBedAssetId] = useState('');
  const [bedLicenseRef, setBedLicenseRef] = useState('');
  const [importKind, setImportKind] = useState('proof_still');
  const [importId, setImportId] = useState('');
  const [importSlug, setImportSlug] = useState('');
  const [importLabel, setImportLabel] = useState('');
  const [importCaption, setImportCaption] = useState('');
  const [importCta, setImportCta] = useState(DEFAULT_COLD_CTA);
  const [importProofIds, setImportProofIds] = useState('');
  const [importLicenseRef, setImportLicenseRef] = useState('');
  const [tiktokMusicSoundId, setTiktokMusicSoundId] = useState('');
  const [tiktokCmlNote, setTiktokCmlNote] = useState('');
  const [instagramAudioId, setInstagramAudioId] = useState('');
  const [facebookSoundCollectionNote, setFacebookSoundCollectionNote] = useState('');
  const [youtubeNote, setYoutubeNote] = useState('');
  const [linkedinNote, setLinkedinNote] = useState('');
  const [starterId, setStarterId] = useState(null);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [panel, setPanel] = useState(null); // 'edit' | 'schedule' | null
  const [showAllPacks, setShowAllPacks] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [burnOverlay, setBurnOverlay] = useState(false);
  const [stockBedId, setStockBedId] = useState('');
  const [overlayStyle, setOverlayStyle] = useState(DEFAULT_OVERLAY_STYLE);
  const [proofSearch, setProofSearch] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [proofLimit, setProofLimit] = useState(8);
  const [showCaptionIdeas, setShowCaptionIdeas] = useState(false);
  const [previewOverlayLive, setPreviewOverlayLive] = useState(false);
  const fileInputRef = useRef(null);

  const {
    data: packsData,
    loading: packsLoading,
    error: packsError,
    refetch: refetchPacks,
  } = useQuery(ADMIN_IHVG_PACKS, { pollInterval: 4000 });

  const {
    data: proofData,
    loading: proofLoading,
    error: proofError,
    refetch: refetchProofs,
  } = useQuery(ADMIN_IHVG_PROOF_CATALOG);

  const { data: clipData, refetch: refetchClips } = useQuery(ADMIN_IHVG_CLIP_PRESETS);
  const { data: bedsData, refetch: refetchBeds } = useQuery(ADMIN_IHVG_AUDIO_BEDS);
  const { data: stockData } = useQuery(ADMIN_IHVG_STOCK_CATALOG, {
    errorPolicy: 'ignore',
  });
  const [loadCaptionIdeas, { data: captionIdeasData, loading: captionIdeasLoading }] = useLazyQuery(
    ADMIN_IHVG_CAPTION_IDEAS,
    { fetchPolicy: 'network-only' },
  );

  const [loadPack, { data: packData, loading: packLoading }] = useLazyQuery(ADMIN_IHVG_PACK, {
    fetchPolicy: 'network-only',
  });

  const [generatePack, { loading: generating }] = useMutation(ADMIN_IHVG_GENERATE_PACK);
  const [updateDraft, { loading: updating }] = useMutation(ADMIN_IHVG_UPDATE_PACK_DRAFT);
  const [approvePack, { loading: approving }] = useMutation(ADMIN_IHVG_APPROVE_PACK);
  const [schedulePack, { loading: scheduling }] = useMutation(ADMIN_IHVG_SCHEDULE_PACK);
  const [retryPublish, { loading: retrying }] = useMutation(ADMIN_IHVG_RETRY_PUBLISH);
  const [ensureDefaults, { loading: seeding }] = useMutation(ADMIN_IHVG_ENSURE_DEFAULTS);
  const [importAsset, { loading: importing }] = useMutation(ADMIN_IHVG_IMPORT_ASSET);

  const packs = useMemo(
    () => packsData?.adminIhvgPacks || [],
    [packsData?.adminIhvgPacks]
  );
  const proofs = useMemo(
    () => proofData?.adminIhvgProofCatalog || [],
    [proofData?.adminIhvgProofCatalog]
  );
  const stockBeds = useMemo(
    () => stockData?.adminIhvgStockCatalog || [],
    [stockData?.adminIhvgStockCatalog]
  );
  const filteredProofs = useMemo(
    () => filterCatalogByQuery(proofs, proofSearch),
    [proofs, proofSearch],
  );
  const visibleProofs = useMemo(
    () => filteredProofs.slice(0, proofLimit),
    [filteredProofs, proofLimit],
  );
  const filteredStockBeds = useMemo(
    () => filterCatalogByQuery(stockBeds, stockSearch),
    [stockBeds, stockSearch],
  );
  const captionIdeas = captionIdeasData?.adminIhvgCaptionIdeas || [];
  const clips = clipData?.adminIhvgClipPresets || [];
  const audioBeds = bedsData?.adminIhvgAudioBeds || [];
  const selected = packData?.adminIhvgPack || packs.find((p) => p.id === selectedId) || null;
  const statusRows = useMemo(() => platformStatusRows(selected?.publishStatus), [selected]);
  const selectedKey = selected ? `${selected.id}:${selected.updatedAt || ''}` : '';
  const [formatFilter, setFormatFilter] = useState('all');
  const filteredPacks = useMemo(
    () => filterPacksByFormat(packs, formatFilter),
    [packs, formatFilter],
  );
  const queue = useMemo(
    () => reviewQueue(filteredPacks, dismissedIds),
    [filteredPacks, dismissedIds],
  );
  const families = useMemo(
    () => reviewFamilies(filteredPacks, dismissedIds),
    [filteredPacks, dismissedIds],
  );
  const selectedFamily = selected ? packFamilyKey(selected) : null;
  const options = useMemo(
    () => (selectedFamily ? variantOptions(filteredPacks, selectedFamily, dismissedIds) : []),
    [filteredPacks, selectedFamily, dismissedIds],
  );
  const familyIndex = Math.max(0, families.findIndex((f) => f === selectedFamily));
  const counts = packCounts(packs);
  const formatChoices = useMemo(() => distinctFormats(packs, dismissedIds), [packs, dismissedIds]);
  const showFormatFilter = formatChoices.length >= 2;
  const schedulePlatforms = platformsForFormat(packFormat(selected));

  useEffect(() => {
    if (!selectedId) return undefined;
    loadPack({ variables: { id: selectedId } });
    const timer = setInterval(() => {
      loadPack({ variables: { id: selectedId } });
    }, 4000);
    return () => clearInterval(timer);
  }, [selectedId, loadPack]);

  // Fetch preview as blob so JSON/error responses never hit <video>/<img> (MIME error).
  // Images use data: URLs (CSP-safe even when img-src lacked blob:); video keeps blob:.
  useEffect(() => {
    let revoked = null;
    let cancelled = false;
    setPreviewError(null);
    setPreviewBlobUrl((prev) => {
      if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    const url = selected?.previewUrl;
    if (!url) return undefined;
    (async () => {
      try {
        const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
        const ct = String(res.headers.get('content-type') || '');
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          if (!cancelled) {
            setPreviewError(text || `Preview failed (${res.status})`);
          }
          return;
        }
        const fmt = packFormat(selected);
        const okVideo = fmt === 'video_reel' && (ct.includes('video') || ct.includes('octet-stream'));
        const okImage = fmt === 'image_post' && (
          ct.includes('image/jpeg') || ct.includes('image/png') || ct.includes('image/webp')
          || ct.includes('image/')
        );
        if (fmt === 'text_post') {
          if (!cancelled) setPreviewError(null);
          return;
        }
        if (!okVideo && !okImage) {
          const text = await res.text().catch(() => '');
          if (!cancelled) {
            setPreviewError(text || `Unexpected preview type: ${ct || 'unknown'}`);
          }
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const src = await mediaBlobToPreviewSrc(blob, { preferDataUrl: okImage });
        if (cancelled) return;
        if (!okImage) revoked = src;
        // Seek past t=0 so Review shows a painted frame (avoids black first-frame flash).
        setPreviewBlobUrl(okVideo && src ? `${src}#t=0.4` : src);
      } catch (err) {
        if (!cancelled) {
          setPreviewError(err.message || 'Could not load preview');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [selected?.previewUrl, selected?.mediaSha256, selected?.id, selected?.contentFormat]);

  useEffect(() => {
    if (!selected) return;
    setCaption(selected.caption || '');
    setCta(selected.cta || '');
    setSelectedProofs(selected.proofIds || []);
    setBurnOverlay(Boolean(selected.burnOverlay));
    setStockBedId(selected.stockBedId || '');
    setOverlayStyle(normalizeOverlayStyleForm(
      selected.overlayStyle || selected.publishStatus?.content?.overlayStyle,
    ));
    setPreviewOverlayLive(false);
    setProofSearch('');
    setStockSearch('');
    setProofLimit(8);
    setShowCaptionIdeas(false);
    setPlatforms(defaultPlatformsForFormat(packFormat(selected)));
    const sounds = selected.platformSounds || {};
    setTiktokMusicSoundId(sounds.tiktokMusicSoundId || '');
    setTiktokCmlNote(sounds.tiktokCmlNote || '');
    setInstagramAudioId(sounds.instagramAudioId || '');
    setFacebookSoundCollectionNote(sounds.facebookSoundCollectionNote || '');
    setYoutubeNote(sounds.youtubeNote || '');
    setLinkedinNote(sounds.linkedinNote || '');
    if (canSchedule(selected) && panel !== 'edit') {
      setPanel('schedule');
    }
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps -- sync form when pack identity/version changes

  useEffect(() => {
    if (proofs.length > 0 && selectedProofs.length === 0 && !selectedId && !starterId) {
      setSelectedProofs([proofs[0].id]);
    }
  }, [proofs, selectedProofs.length, selectedId, starterId]);

  useEffect(() => {
    if (section !== 'review') return;
    if (selectedId && queue.some((p) => p.id === selectedId)) return;
    if (selectedId && canSchedule(packs.find((p) => p.id === selectedId))) return;
    if (queue[0]) {
      setSelectedId(queue[0].id);
      setPanel(null);
    }
  }, [section, queue, selectedId, packs]);

  useEffect(() => {
    if (seedAttempted || packsLoading || !isAdmin) return;
    // One-shot ensure(refill:false) even when video Review is non-empty so
    // image/text stable slugs seed when IHVG_MULTI_FORMAT is on.
    setSeedAttempted(true);
    const hadReviewable = reviewQueue(packs).length > 0;
    if (!hadReviewable) setActionMessage('Generating ideas…');
    ensureDefaults({ variables: { refill: false } })
      .then(async (result) => {
        const payload = result?.data?.adminIhvgEnsureDefaults;
        await Promise.all([refetchPacks(), refetchProofs(), refetchClips()]);
        const msg = ensureIdeasMessage(result);
        if (!hadReviewable || (payload?.created?.length || 0) > 0) {
          setActionMessage(msg);
        }
        const firstId = payload?.composed?.[0]?.id
          || payload?.created?.[0]?.id
          || null;
        if (firstId && !hadReviewable) {
          setSelectedId(firstId);
          setDismissedIds([]);
          setSection('review');
        }
      })
      .catch((err) => {
        setActionMessage(mutationMessage(err));
      });
  }, [
    seedAttempted,
    packsLoading,
    packs,
    isAdmin,
    ensureDefaults,
    refetchPacks,
    refetchProofs,
    refetchClips,
  ]);

  async function generateIdeas({ refill = false } = {}) {
    setActionMessage(null);
    setDismissedIds([]);
    try {
      const raw = await ensureDefaults({ variables: { refill } });
      const payload = raw?.data?.adminIhvgEnsureDefaults;
      const firstId = payload?.refilled?.[0]?.id
        || payload?.composed?.[0]?.id
        || payload?.created?.[0]?.id
        || null;
      await refreshAll(firstId);
      setActionMessage(ensureIdeasMessage(raw));
      if (firstId) {
        setSelectedId(firstId);
        setSection('review');
        setPanel(null);
      }
    } catch (err) {
      setActionMessage(mutationMessage(err));
      await refreshAll(selectedId);
    }
  }

  async function refreshAll(id = selectedId) {
    await Promise.all([refetchPacks(), refetchProofs(), refetchClips(), refetchBeds()]);
    if (id) await loadPack({ variables: { id } });
  }

  async function runAction(label, fn) {
    setActionMessage(null);
    try {
      const result = await fn();
      const pack = result?.data ? Object.values(result.data)[0] : null;
      const id = pack?.id || pack?.pack?.id || selectedId;
      if (id) setSelectedId(id);
      await refreshAll(id);
      if (label === 'Schedule' || label === 'Retry publish') {
        setActionMessage(scheduleOutcomeMessage(pack));
      } else if (pack?.message) {
        setActionMessage(pack.message);
      } else {
        setActionMessage(`${label} OK`);
      }
      return pack;
    } catch (err) {
      const extras = err.graphQLErrors?.[0]?.extensions?.publishStatus
        || err.graphQLErrors?.[0]?.extensions?.exception?.publishStatus;
      if (extras) {
        setActionMessage(scheduleOutcomeMessage({ publishStatus: extras }));
      } else {
        setActionMessage(mutationMessage(err));
      }
      await refreshAll(selectedId);
      return null;
    }
  }

  function togglePlatform(name) {
    setPlatforms((prev) =>
      prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]
    );
  }

  function toggleProof(id) {
    setSelectedProofs((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  }

  function applyClipPreset(clip) {
    setStarterId(clip.id);
    setSelectedProofs([clip.proofId]);
    if (clip.suggestedCaption) setCaption(clip.suggestedCaption);
    if (clip.suggestedCta) setCta(clip.suggestedCta);
    setSlug(autoSlugFromCaption(clip.suggestedCaption, clip.proofId));
    setAudioMode('silent');
    setSection('create');
    setActionMessage(null);
  }

  function applyBlankStarter() {
    setStarterId('blank');
    setCaption('');
    setSlug('');
    if (proofs[0]) setSelectedProofs([proofs[0].id]);
    setAudioMode('silent');
    setSection('create');
  }

  function rejectCurrent() {
    if (!selectedId || !selected) return;
    const family = packFamilyKey(selected);
    const siblingIds = variantOptions(packs, family).map((p) => p.id);
    const remaining = queue.filter((p) => !siblingIds.includes(p.id) && p.id !== selectedId);
    const nextId = remaining[0]?.id || null;
    setDismissedIds((prev) => {
      const next = new Set(prev);
      siblingIds.forEach((id) => next.add(id));
      next.add(selectedId);
      return [...next];
    });
    setPanel(null);
    setSelectedId(nextId);
    setActionMessage('Skipped idea — next');
  }

  function openEdit() {
    setPanel('edit');
  }

  function closePanel() {
    setPanel(null);
  }

  async function handleApprove() {
    if (!selected) return;
    const pack = await runAction('APPROVE', () =>
      approvePack({ variables: { id: selected.id } })
    );
    if (pack) setPanel('schedule');
  }

  const busy = generating || updating || approving || scheduling || retrying || seeding || importing;
  const queryError = packsError || proofError;
  const createReady =
    Boolean(caption.trim())
    && selectedProofs.length > 0
    && (audioMode !== 'royalty_free_bed' || (bedAssetId && bedLicenseRef.trim()));

  function platformSoundsInput() {
    return {
      tiktokMusicSoundId: tiktokMusicSoundId || null,
      tiktokCmlNote: tiktokCmlNote || null,
      instagramAudioId: instagramAudioId || null,
      facebookSoundCollectionNote: facebookSoundCollectionNote || null,
      youtubeNote: youtubeNote || null,
      linkedinNote: linkedinNote || null,
    };
  }

  function SoundsFields() {
    return (
      <>
        <p className="chart-hint">{PLATFORM_SOUND_CHECKLIST}</p>
        <label>
          TikTok music / CML id
          <input
            value={tiktokMusicSoundId}
            onChange={(e) => setTiktokMusicSoundId(e.target.value)}
            placeholder="optional"
          />
        </label>
        <label>
          Instagram audio id
          <input
            value={instagramAudioId}
            onChange={(e) => setInstagramAudioId(e.target.value)}
            placeholder="optional"
          />
        </label>
        <label>
          Notes (TikTok / FB / YT / LinkedIn)
          <textarea
            rows={2}
            value={[tiktokCmlNote, facebookSoundCollectionNote, youtubeNote, linkedinNote]
              .filter(Boolean)
              .join('\n')}
            onChange={(e) => {
              const lines = e.target.value.split('\n');
              setTiktokCmlNote(lines[0] || '');
              setFacebookSoundCollectionNote(lines[1] || '');
              setYoutubeNote(lines[2] || '');
              setLinkedinNote(lines[3] || '');
            }}
            placeholder="Optional one-line notes per platform"
          />
        </label>
        <div className="ihvg-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={busy || !selected}
            onClick={() =>
              runAction('Save sounds', () =>
                updateDraft({
                  variables: {
                    id: selected.id,
                    input: { platformSounds: platformSoundsInput() },
                  },
                })
              )
            }
          >
            Save sounds
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={async () => {
              const text = selected?.platformSoundsChecklist || PLATFORM_SOUND_CHECKLIST;
              try {
                await navigator.clipboard.writeText(text);
                setActionMessage('Checklist copied');
              } catch {
                setActionMessage(text);
              }
            }}
          >
            Copy checklist
          </button>
        </div>
      </>
    );
  }

  return (
    <ModuleShell
      title="Social content"
      description={
        <span>
          Social ideas (video, image, caption) from Fiscal Flowcast product knowledge —
          decision fork → Basic yearly, cash-forward, multi-angle hubs. Manual create is optional.
          {' '}
          <span className="ihvg-sandbox-pill">sandbox</span>
        </span>
      }
      leading={
        <nav className="experiment-breadcrumb" aria-label="Breadcrumb">
          <Link to="/growth">Growth</Link>
          <span aria-hidden="true"> / </span>
          <span>Social content</span>
        </nav>
      }
      actions={
        <button type="button" className="secondary-button" onClick={() => refreshAll()}>
          Refresh
        </button>
      }
      loading={packsLoading && packs.length === 0 && !seedAttempted}
      loadingLabel="Loading videos…"
      error={queryError}
      errorLabel={ihvgLoadErrorLabel(queryError)}
    >
      {actionMessage ? (
        <p
          className={
            /fail|error|invalid|could not/i.test(actionMessage)
              ? 'module-status module-status-error'
              : 'module-status'
          }
          role="status"
        >
          {actionMessage}
        </p>
      ) : null}

      <SectionTabs
        active={section}
        onChange={(next) => {
          setSection(next);
          if (next === 'review') setPanel(null);
        }}
      />

      {section === 'create' ? (
        <div
          className="ihvg-panel ihvg-create-simple"
          role="tabpanel"
          id="ihvg-panel-create"
          aria-labelledby="ihvg-tab-create"
        >
          <section className="chart-card ihvg-create-card">
            <h2>New (optional)</h2>
            <p className="chart-hint">
              Manual path — pick a starter or blank, write a caption, generate.
              Default path is Review → Generated for you. Cold CTA default is Basic yearly
              (or a decision hub), not Free-only.
            </p>
            <p className="chart-hint ihvg-positioning-hint">{REVIEW_POSITIONING_HINT}</p>

            {!isAdmin ? (
              <p className="chart-hint">STAFF is read-only. ADMIN required to generate.</p>
            ) : (
              <>
                <div className="ihvg-starter-grid" role="list">
                  {clips.map((clip) => (
                    <button
                      key={clip.id}
                      type="button"
                      role="listitem"
                      className={
                        starterId === clip.id
                          ? 'ihvg-starter ihvg-starter-active'
                          : 'ihvg-starter'
                      }
                      onClick={() => applyClipPreset(clip)}
                    >
                      <span className="ihvg-starter-label">{clip.label}</span>
                      <span className="ihvg-starter-caption">
                        {clip.suggestedCaption || 'Starter clip'}
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    role="listitem"
                    className={
                      starterId === 'blank'
                        ? 'ihvg-starter ihvg-starter-active'
                        : 'ihvg-starter'
                    }
                    onClick={applyBlankStarter}
                  >
                    <span className="ihvg-starter-label">New blank</span>
                    <span className="ihvg-starter-caption">Write your own caption</span>
                  </button>
                </div>

                {starterId ? (
                  <form
                    className="ihvg-form ihvg-create-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const resolvedSlug =
                        slug.trim() || autoSlugFromCaption(caption, selectedProofs[0]);
                      runAction('Generate', () =>
                        generatePack({
                          variables: {
                            input: {
                              slug: resolvedSlug,
                              proofIds: selectedProofs,
                              caption: caption || undefined,
                              cta: cta || undefined,
                              audioMode,
                              bedAssetId:
                                audioMode === 'royalty_free_bed'
                                  ? bedAssetId || undefined
                                  : undefined,
                              bedLicenseRef:
                                audioMode === 'royalty_free_bed'
                                  ? bedLicenseRef || undefined
                                  : undefined,
                            },
                          },
                        })
                      ).then((pack) => {
                        if (pack?.id) {
                          setSelectedId(pack.id);
                          setDismissedIds((prev) => prev.filter((id) => id !== pack.id));
                          setPanel(null);
                          setSection('review');
                        }
                      });
                    }}
                  >
                    <label>
                      Caption
                      <textarea
                        rows={3}
                        required
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Fork + consequence + quiet Basic yearly ask"
                      />
                    </label>
                    <button
                      type="submit"
                      className="primary-button ihvg-generate-btn"
                      disabled={busy || !createReady}
                    >
                      {generating ? 'Generating…' : 'Generate'}
                    </button>

                    <Disclosure summary="Advanced">
                      <label>
                        Slug
                        <input
                          value={slug}
                          onChange={(e) => setSlug(e.target.value)}
                          placeholder="auto from caption if empty"
                        />
                      </label>
                      <label>
                        CTA URL
                        <input
                          value={cta}
                          onChange={(e) => setCta(e.target.value)}
                          placeholder={DEFAULT_COLD_CTA}
                        />
                      </label>
                      <fieldset>
                        <legend>Stills</legend>
                        {proofLoading ? (
                          <p className="chart-hint">Loading…</p>
                        ) : proofs.length === 0 ? (
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => setSection('import')}
                          >
                            Import stills
                          </button>
                        ) : (
                          <div className="ihvg-proof-grid">
                            {proofs.map((proof) => (
                              <label key={proof.id} className="ihvg-check ihvg-proof-card">
                                <input
                                  type="checkbox"
                                  checked={selectedProofs.includes(proof.id)}
                                  onChange={() => toggleProof(proof.id)}
                                />
                                <span>{proof.label || proof.id}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </fieldset>
                      <fieldset>
                        <legend>Audio</legend>
                        <p className="chart-hint">Default silent. RF bed only with a licensed import.</p>
                        <div className="ihvg-platform-row">
                          {AUDIO_MODES.map((mode) => (
                            <label key={mode.id} className="ihvg-check">
                              <input
                                type="radio"
                                name="ihvg-audio-mode"
                                checked={audioMode === mode.id}
                                onChange={() => setAudioMode(mode.id)}
                              />
                              {mode.label}
                            </label>
                          ))}
                        </div>
                        {audioMode === 'royalty_free_bed' ? (
                          <>
                            {audioBeds.length === 0 ? (
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => {
                                  setImportKind('audio_bed');
                                  setSection('import');
                                }}
                              >
                                Import licensed bed
                              </button>
                            ) : (
                              <label>
                                Audio bed
                                <select
                                  value={bedAssetId}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    setBedAssetId(next);
                                    const bed = audioBeds.find((row) => row.id === next);
                                    if (bed?.licenseRef) setBedLicenseRef(bed.licenseRef);
                                  }}
                                  required
                                >
                                  <option value="">Select bed…</option>
                                  {audioBeds.map((bed) => (
                                    <option key={bed.id} value={bed.id}>
                                      {bed.label || bed.id}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            )}
                            <label>
                              License ref
                              <input
                                required={audioMode === 'royalty_free_bed'}
                                value={bedLicenseRef}
                                onChange={(e) => setBedLicenseRef(e.target.value)}
                                placeholder="License source URL / title"
                              />
                            </label>
                          </>
                        ) : null}
                      </fieldset>
                    </Disclosure>
                  </form>
                ) : (
                  <p className="chart-hint ihvg-create-hint">
                    {clips.length === 0
                      ? 'No starters yet — open Import to seed defaults, or choose New blank.'
                      : 'Choose a starter above to continue.'}
                  </p>
                )}
              </>
            )}
          </section>
        </div>
      ) : null}

      {section === 'review' ? (
        <div
          className="ihvg-panel ihvg-review-deck"
          role="tabpanel"
          id="ihvg-panel-review"
          aria-labelledby="ihvg-tab-review"
        >
          <div className="ihvg-deck-toolbar">
            <span className="ihvg-deck-progress">
              {families.length
                ? `Generated for you · ${Math.min(familyIndex + 1, families.length)}/${families.length} ideas`
                : counts.approved
                  ? `${counts.approved} approved — schedule next`
                  : 'Queue empty'}
              {options.length > 1 ? ` · ${options.length} options` : null}
            </span>
            <div className="ihvg-actions">
              {isAdmin && queue.length === 0 ? (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={busy}
                  onClick={() => generateIdeas({ refill: true })}
                >
                  {seeding ? 'Generating…' : 'Generate ideas'}
                </button>
              ) : null}
              <button
                type="button"
                className="secondary-button"
                onClick={() => setShowAllPacks((v) => !v)}
              >
                {showAllPacks ? 'Hide list' : 'All packs'}
              </button>
            </div>
          </div>
          {queue.length > 0 ? (
            <p className="chart-hint ihvg-positioning-hint">{REVIEW_POSITIONING_HINT}</p>
          ) : null}
          {showFormatFilter ? (
            <div className="ihvg-format-filter" role="group" aria-label="Format filter">
              {[
                ['all', 'All'],
                ['video_reel', 'Video'],
                ['image_post', 'Image'],
                ['text_post', 'Text'],
              ].filter(([id]) => id === 'all' || formatChoices.includes(id)).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={formatFilter === id ? 'ihvg-format-chip ihvg-format-chip-active' : 'ihvg-format-chip'}
                  onClick={() => setFormatFilter(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          {showAllPacks ? (
            <ul className="ihvg-pack-list ihvg-pack-list-compact">
              {packs.map((pack) => (
                <li key={pack.id}>
                  <button
                    type="button"
                    className={
                      pack.id === selectedId
                        ? 'ihvg-pack-item ihvg-pack-item-active'
                        : 'ihvg-pack-item'
                    }
                    onClick={() => {
                      setSelectedId(pack.id);
                      setDismissedIds((prev) => prev.filter((id) => id !== pack.id));
                      setPanel(canSchedule(pack) ? 'schedule' : null);
                    }}
                  >
                    <span className="ihvg-pack-slug">{pack.slug}</span>
                    <PackStatusBadge status={pack.status} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {!selectedId && queue.length === 0 ? (
            <div className="ihvg-empty ihvg-empty-center">
              <p className="chart-empty">No ideas in the queue.</p>
              <p className="chart-hint">
                Generate short-form social ideas from product knowledge (fork → Basic yearly), or write your own.
              </p>
              <p className="chart-hint ihvg-positioning-hint">{REVIEW_POSITIONING_HINT}</p>
              <div className="ihvg-actions">
                {isAdmin ? (
                  <button
                    type="button"
                    className="primary-button"
                    disabled={busy}
                    onClick={() => generateIdeas({ refill: true })}
                  >
                    {seeding ? 'Generating…' : 'Generate ideas'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setSection('create')}
                >
                  New (manual)
                </button>
              </div>
            </div>
          ) : packLoading && !selected ? (
            <p className="chart-hint">Loading…</p>
          ) : !selected ? (
            <p className="chart-empty">Pack not found.</p>
          ) : panel === 'schedule' && canSchedule(selected) ? (
            <section className="chart-card ihvg-schedule-step">
              <h2>Schedule</h2>
              <p className="chart-hint">
                Approved. Pick platforms and a time.
                {' '}
                <span className="ihvg-sandbox-pill">sandbox</span>
              </p>
              {packFormat(selected) === 'text_post' ? (
                <div className="ihvg-preview ihvg-preview-sm ihvg-preview-placeholder">
                  <p className="ihvg-caption-preview">{selected.caption}</p>
                  <p className="chart-hint">{selected.cta}</p>
                </div>
              ) : previewBlobUrl && packFormat(selected) === 'image_post' ? (
                <img
                  className="ihvg-preview ihvg-preview-sm"
                  key={`${selected.id}:${selected.mediaSha256 || ''}:sched`}
                  src={previewBlobUrl}
                  alt=""
                />
              ) : previewBlobUrl ? (
                <video
                  className="ihvg-preview ihvg-preview-sm"
                  key={`${selected.id}:${selected.mediaSha256 || ''}:sched`}
                  src={previewBlobUrl}
                  controls
                  playsInline
                >
                  <track kind="captions" />
                </video>
              ) : null}
              {isAdmin ? (
                <>
                  <div className="ihvg-platform-row">
                    {schedulePlatforms.map((name) => (
                      <label key={name} className="ihvg-check">
                        <input
                          type="checkbox"
                          checked={platforms.includes(name)}
                          onChange={() => togglePlatform(name)}
                        />
                        {name}
                      </label>
                    ))}
                  </div>
                  <label>
                    When
                    <input
                      type="datetime-local"
                      value={scheduledLocal}
                      onChange={(e) => setScheduledLocal(e.target.value)}
                    />
                  </label>
                  <div className="ihvg-actions">
                    <button
                      type="button"
                      className="primary-button"
                      disabled={busy || platforms.length === 0}
                      onClick={() => {
                        const scheduledAt = localInputToIso(scheduledLocal);
                        if (!scheduledAt) {
                          setActionMessage('Invalid schedule time');
                          return;
                        }
                        runAction('Schedule', () =>
                          schedulePack({
                            variables: {
                              input: {
                                packId: selected.id,
                                platforms,
                                scheduledAt,
                              },
                            },
                          })
                        ).then((pack) => {
                          if (pack) {
                            const nextId = nextReviewId(queue, selected.id);
                            setDismissedIds((prev) =>
                              prev.includes(selected.id) ? prev : [...prev, selected.id]
                            );
                            if (nextId) {
                              setSelectedId(nextId);
                              setPanel(null);
                            }
                          }
                        });
                      }}
                    >
                      {scheduling ? 'Scheduling…' : 'Schedule'}
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={busy}
                      onClick={() =>
                        runAction('Retry publish', () =>
                          retryPublish({
                            variables: {
                              packId: selected.id,
                              platforms: platforms.length > 0 ? platforms : undefined,
                            },
                          })
                        )
                      }
                    >
                      Retry failed
                    </button>
                    <button type="button" className="secondary-button" onClick={closePanel}>
                      Back to deck
                    </button>
                  </div>
                  <Disclosure summary="Sounds (optional)">
                    <SoundsFields />
                  </Disclosure>
                </>
              ) : (
                <p className="chart-hint">Read-only (STAFF).</p>
              )}
              {statusRows.length > 0 ? (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th scope="col">Platform</th>
                        <th scope="col">Status</th>
                        <th scope="col">External id</th>
                        <th scope="col">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusRows.map((row) => (
                        <tr key={row.platform}>
                          <td>{row.platform}</td>
                          <td><PackStatusBadge status={row.status} /></td>
                          <td>{row.externalId || '—'}</td>
                          <td>{row.error || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              <p className="chart-hint ihvg-footnote">{SANDBOX_SCHEDULE_NOTE}</p>
            </section>
          ) : (
            <div className="ihvg-deck-stage">
              <div className="ihvg-deck-stack" aria-hidden={panel === 'edit'}>
                <div className="ihvg-deck-card ihvg-deck-card-back" />
                <div className="ihvg-deck-card">
                  {selected.composeError ? (
                    <p className="module-status module-status-error" role="alert">
                      {selected.composeError}
                    </p>
                  ) : null}
                  {packFormat(selected) === 'text_post' ? (
                    <div className="ihvg-preview ihvg-preview-deck ihvg-preview-placeholder">
                      <p className="ihvg-format-badge">{packFormatLabel('text_post')}</p>
                      <p className="ihvg-caption-preview">{selected.caption}</p>
                      <p className="chart-hint">{selected.cta}</p>
                    </div>
                  ) : previewBlobUrl && packFormat(selected) === 'image_post' ? (
                    <>
                      <p className="ihvg-format-badge">{packFormatLabel('image_post')}</p>
                      <div className="ihvg-preview-wrap">
                        <img
                          className="ihvg-preview ihvg-preview-deck"
                          key={`${selected.id}:${selected.mediaSha256 || ''}:blob`}
                          src={previewBlobUrl}
                          alt=""
                          onError={() => {
                            setPreviewError(
                              'Image preview blocked or unreadable (check CSP img-src for blob:/data:)',
                            );
                            setPreviewBlobUrl((prev) => {
                              if (prev && String(prev).startsWith('blob:')) {
                                URL.revokeObjectURL(prev);
                              }
                              return null;
                            });
                          }}
                        />
                        {(previewOverlayLive || burnOverlay) ? (
                          <div
                            className="ihvg-preview-overlay"
                            style={overlayStyleCssVars(overlayStyle)}
                            aria-hidden="true"
                          >
                            <p className="ihvg-preview-overlay-hook">
                              {String(
                                selected.publishStatus?.content?.hook
                                || selected.caption
                                || '',
                              )
                                .replace(/[—–]/g, '-')
                                .trim()
                                .slice(0, 220)}
                            </p>
                            <p className="ihvg-preview-overlay-cta">
                              {selected.publishStatus?.content?.ctaLabel || DEFAULT_COLD_CTA_LABEL}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : previewBlobUrl ? (
                    <>
                      <p className="ihvg-format-badge">{packFormatLabel(packFormat(selected))}</p>
                      <div className="ihvg-preview-wrap">
                        <video
                          className="ihvg-preview ihvg-preview-deck"
                          key={`${selected.id}:${selected.mediaSha256 || ''}:blob`}
                          src={previewBlobUrl}
                          controls
                          playsInline
                          autoPlay
                          muted
                          loop
                          onError={() => {
                            setPreviewError(
                              'Preview blocked or unreadable (check CSP media-src / media bytes)',
                            );
                            setPreviewBlobUrl((prev) => {
                              if (prev) URL.revokeObjectURL(prev);
                              return null;
                            });
                          }}
                        >
                          <track kind="captions" />
                        </video>
                        {panel === 'edit' && selected.stockBedId ? (
                          <div
                            className="ihvg-preview-overlay ihvg-preview-overlay-live"
                            style={overlayStyleCssVars(overlayStyle)}
                            aria-hidden="true"
                          >
                            <p className="ihvg-preview-overlay-hook">
                              {String(
                                selected.publishStatus?.content?.hook
                                || selected.caption
                                || '',
                              )
                                .replace(/[—–]/g, '-')
                                .trim()
                                .slice(0, 220)}
                            </p>
                            <p className="ihvg-preview-overlay-cta">
                              {selected.publishStatus?.content?.ctaLabel || DEFAULT_COLD_CTA_LABEL}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : (
                    <div className="ihvg-preview ihvg-preview-deck ihvg-preview-placeholder">
                      <p className="ihvg-format-badge">{packFormatLabel(packFormat(selected))}</p>
                      <p>
                        {selected.status === 'composing'
                          ? 'Composing…'
                          : previewError
                            ? previewError
                            : selected.previewUrl
                              ? 'Loading preview…'
                              : packFormat(selected) === 'text_post'
                                ? selected.caption || 'Caption pack'
                                : 'No preview yet'}
                      </p>
                      {selected.status === 'draft' && !selected.mediaSha256 ? (
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => generateIdeas({ refill: false })}
                        >
                          Compose preview
                        </button>
                      ) : null}
                    </div>
                  )}
                  {options.length > 1 ? (
                    <div className="ihvg-option-row" role="group" aria-label="Options for this idea">
                      {options.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          className={
                            opt.id === selected.id
                              ? 'ihvg-option ihvg-option-active'
                              : 'ihvg-option'
                          }
                          onClick={() => {
                            setSelectedId(opt.id);
                            setPanel(null);
                          }}
                        >
                          {packVariantLabel(opt)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <p className="ihvg-deck-caption">
                    {selected.caption || 'No caption'}
                  </p>
                </div>
              </div>

              {isAdmin ? (
                <div className="ihvg-deck-actions" role="group" aria-label="Review actions">
                  <button
                    type="button"
                    className="ihvg-action-reject"
                    disabled={busy}
                    aria-label="Reject"
                    title="Reject"
                    onClick={rejectCurrent}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                  <button
                    type="button"
                    className="ihvg-action-edit"
                    disabled={busy}
                    onClick={openEdit}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="ihvg-action-approve"
                    disabled={busy || !canApprove(selected)}
                    aria-label="Approve"
                    title="Approve"
                    onClick={handleApprove}
                  >
                    <span aria-hidden="true">✓</span>
                  </button>
                </div>
              ) : (
                <p className="chart-hint">Read-only (STAFF).</p>
              )}

              {panel === 'edit' ? (
                <section className="chart-card ihvg-edit-panel" aria-label="Edit idea">
                  <div className="ihvg-edit-head">
                    <h2>Edit option</h2>
                    <button type="button" className="secondary-button" onClick={closePanel}>
                      Close
                    </button>
                  </div>
                  {options.length > 1 ? (
                    <fieldset>
                      <legend>Switch option</legend>
                      <div className="ihvg-option-row">
                        {options.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            className={
                              opt.id === selected.id
                                ? 'ihvg-option ihvg-option-active'
                                : 'ihvg-option'
                            }
                            onClick={() => setSelectedId(opt.id)}
                          >
                            {packVariantLabel(opt)}
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}
                  <label>
                    Caption
                    <textarea
                      rows={3}
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                    />
                  </label>
                  <div className="ihvg-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={busy || captionIdeasLoading}
                      onClick={() => {
                        setShowCaptionIdeas(true);
                        loadCaptionIdeas({
                          variables: {
                            family: packFamily(selected),
                            format: packFormat(selected),
                            limit: 8,
                          },
                        });
                      }}
                    >
                      {captionIdeasLoading ? 'Brainstorming…' : 'Brainstorm captions'}
                    </button>
                  </div>
                  {showCaptionIdeas ? (
                    <fieldset>
                      <legend>Caption ideas (tap to apply — still editable)</legend>
                      <div className="ihvg-option-row">
                        {captionIdeas.map((idea) => (
                          <button
                            key={idea.id}
                            type="button"
                            className="ihvg-option"
                            onClick={() => {
                              setCaption(idea.caption || '');
                              if (idea.cta) setCta(idea.cta);
                            }}
                          >
                            {idea.label}
                          </button>
                        ))}
                        {!captionIdeasLoading && captionIdeas.length === 0 ? (
                          <p className="chart-hint">No ideas for this family yet.</p>
                        ) : null}
                      </div>
                    </fieldset>
                  ) : null}
                  <label>
                    CTA
                    <input
                      value={cta}
                      onChange={(e) => setCta(e.target.value)}
                      placeholder={DEFAULT_COLD_CTA}
                    />
                  </label>
                  <p className="chart-hint">
                    Prefer apex `/`, decision hubs, or Basic yearly signup — not Free-only, `/pricing`, or wizards.
                  </p>
                  {packFormat(selected) === 'image_post' ? (
                    <label className="ihvg-check">
                      <input
                        type="checkbox"
                        checked={burnOverlay}
                        onChange={(e) => {
                          setBurnOverlay(e.target.checked);
                          setPreviewOverlayLive(e.target.checked);
                        }}
                      />
                      Burn outlined caption onto still (optional)
                    </label>
                  ) : null}
                  {(
                    (packFormat(selected) === 'image_post' && burnOverlay)
                    || (packFormat(selected) === 'video_reel' && selected.stockBedId)
                  ) ? (
                    <fieldset className="ihvg-overlay-style">
                      <legend>Overlay style</legend>
                      <label>
                        Font
                        <select
                          value={overlayStyle.fontId}
                          onChange={(e) => {
                            setOverlayStyle((prev) => ({ ...prev, fontId: e.target.value }));
                            if (packFormat(selected) === 'image_post') {
                              setPreviewOverlayLive(true);
                            }
                          }}
                        >
                          {OVERLAY_FONT_OPTIONS.map((font) => (
                            <option key={font.id} value={font.id}>
                              {font.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="ihvg-overlay-colors">
                        <label>
                          Fill
                          <input
                            type="color"
                            value={overlayStyle.fillColor}
                            onChange={(e) => {
                              setOverlayStyle((prev) => ({
                                ...prev,
                                fillColor: e.target.value.toUpperCase(),
                              }));
                              if (packFormat(selected) === 'image_post') {
                                setPreviewOverlayLive(true);
                              }
                            }}
                          />
                        </label>
                        <label>
                          Outline
                          <input
                            type="color"
                            value={overlayStyle.outlineColor}
                            onChange={(e) => {
                              setOverlayStyle((prev) => ({
                                ...prev,
                                outlineColor: e.target.value.toUpperCase(),
                              }));
                              if (packFormat(selected) === 'image_post') {
                                setPreviewOverlayLive(true);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <p className="chart-hint">
                        Live preview updates immediately. Save rematerializes the still or
                        recomposes Stock with the chosen font and colors.
                      </p>
                    </fieldset>
                  ) : null}
                  {packFormat(selected) === 'video_reel' && selected.stockBedId ? (
                    <fieldset>
                      <legend>Stock beds</legend>
                      <label>
                        Search beds
                        <input
                          value={stockSearch}
                          onChange={(e) => setStockSearch(e.target.value)}
                          placeholder="desk, home, grocery…"
                        />
                      </label>
                      <div className="ihvg-platform-row">
                        {filteredStockBeds.map((bed) => (
                          <label key={bed.id} className="ihvg-check">
                            <input
                              type="radio"
                              name="ihvg-stock-bed"
                              checked={stockBedId === bed.id}
                              onChange={() => setStockBedId(bed.id)}
                            />
                            {bed.label || bed.id}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}
                  {packFormat(selected) !== 'text_post' ? (
                    <fieldset>
                      <legend>Stills</legend>
                      <label>
                        Search stills
                        <input
                          value={proofSearch}
                          onChange={(e) => {
                            setProofSearch(e.target.value);
                            setProofLimit(8);
                          }}
                          placeholder="forecast, tax, decision…"
                        />
                      </label>
                      <div className="ihvg-platform-row">
                        {visibleProofs.map((proof) => (
                          <label key={proof.id} className="ihvg-check">
                            <input
                              type="checkbox"
                              checked={selectedProofs.includes(proof.id)}
                              onChange={() => toggleProof(proof.id)}
                            />
                            {proof.label || proof.id}
                          </label>
                        ))}
                      </div>
                      {filteredProofs.length > proofLimit ? (
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => setProofLimit((n) => n + 8)}
                        >
                          Load more stills ({filteredProofs.length - proofLimit} more)
                        </button>
                      ) : null}
                    </fieldset>
                  ) : null}
                      <p className="chart-hint">
                    {packFormat(selected) === 'text_post'
                      ? 'Save updates the caption hash (no video encode).'
                      : packFormat(selected) === 'image_post'
                        ? 'Save rematerializes the still (overlay font/colors burn with ffmpeg when enabled).'
                        : 'Save recomposes when caption, stills, bed, or overlay style change.'}
                  </p>
                  <div className="ihvg-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={
                        busy
                        || (
                          packFormat(selected) === 'image_post'
                          && selectedProofs.length === 0
                        )
                        || (
                          packFormat(selected) === 'video_reel'
                          && !selected.stockBedId
                          && selectedProofs.length === 0
                        )
                      }
                      onClick={() =>
                        runAction('Save draft', () =>
                          updateDraft({
                            variables: {
                              id: selected.id,
                              input: {
                                caption,
                                cta,
                                ...(packFormat(selected) === 'text_post'
                                  ? {}
                                  : packFormat(selected) === 'video_reel' && selected.stockBedId
                                    ? {}
                                    : { proofIds: selectedProofs }),
                                ...(packFormat(selected) === 'image_post'
                                  ? {
                                    burnOverlay,
                                    proofIds: selectedProofs,
                                    ...(burnOverlay
                                      ? { overlayStyle: normalizeOverlayStyleForm(overlayStyle) }
                                      : {}),
                                  }
                                  : {}),
                                ...(packFormat(selected) === 'video_reel' && selected.stockBedId
                                  ? {
                                    stockBedId: stockBedId || selected.stockBedId,
                                    overlayStyle: normalizeOverlayStyleForm(overlayStyle),
                                  }
                                  : {}),
                              },
                            },
                          })
                        )
                      }
                    >
                      {updating
                        ? 'Saving…'
                        : packFormat(selected) === 'video_reel'
                          ? 'Save & recompose'
                          : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="primary-button"
                      disabled={busy || !canApprove(selected)}
                      onClick={handleApprove}
                    >
                      Approve
                    </button>
                  </div>
                  <Disclosure summary="Sounds (optional)">
                    <SoundsFields />
                  </Disclosure>
                  <Disclosure summary="Details">
                    <p className="ihvg-meta">
                      <strong>{selected.slug}</strong>
                      {' · '}
                      <PackStatusBadge status={selected.status} />
                      {selectedFamily
                        ? ` · ${packFormatLabel(packFormat(selected))} · ${packFamily(selected)}`
                        : null}
                      {selected.updatedAt ? ` · ${formatDateTime(selected.updatedAt)}` : null}
                    </p>
                  </Disclosure>
                </section>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {section === 'import' ? (
        <div
          className="ihvg-panel ihvg-import-layout"
          role="tabpanel"
          id="ihvg-panel-import"
          aria-labelledby="ihvg-tab-import"
        >
          <section className="chart-card">
            <h2>Generate ideas</h2>
            <p className="chart-hint">
              Seeds the product-knowledge bank and composes short-form previews (capped).
              Use refill when the Review queue is empty after reject/approve.
            </p>
            {isAdmin ? (
              <div className="ihvg-actions">
                <button
                  type="button"
                  className="primary-button"
                  disabled={busy}
                  onClick={() => generateIdeas({ refill: false })}
                >
                  {seeding ? 'Working…' : 'Compose bank'}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={busy}
                  onClick={() => generateIdeas({ refill: true })}
                >
                  {seeding ? 'Working…' : 'Generate ideas (refill)'}
                </button>
              </div>
            ) : (
              <p className="chart-hint">ADMIN required to generate.</p>
            )}

            <h3 className="ihvg-subhead">Catalog</h3>
            <ul className="ihvg-import-catalog">
              {proofs.map((proof) => (
                <li key={proof.id}>
                  <span className="ihvg-pack-slug">{proof.label || proof.id}</span>
                  <PackStatusBadge status={proof.source || 'bundled'} />
                </li>
              ))}
            </ul>
          </section>

          <section className="chart-card">
            <h2>Import material</h2>
            <p className="chart-hint">Stills, templates, masters, or licensed audio beds.</p>
            {!isAdmin ? (
              <p className="chart-hint">ADMIN required to import.</p>
            ) : (
              <form
                className="ihvg-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  const file = fileInputRef.current?.files?.[0];
                  let base64;
                  if (
                    importKind === 'proof_still'
                    || importKind === 'master'
                    || importKind === 'audio_bed'
                  ) {
                    if (!file) {
                      setActionMessage('Choose a file to import');
                      return;
                    }
                    base64 = await readFileAsBase64(file);
                  }
                  const proofIds = importProofIds
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                  runAction('Import', () =>
                    importAsset({
                      variables: {
                        input: {
                          kind: importKind,
                          id: importId || undefined,
                          slug: importSlug || undefined,
                          label: importLabel || undefined,
                          caption: importCaption || undefined,
                          cta: importCta || undefined,
                          proofIds: proofIds.length ? proofIds : undefined,
                          licenseRef:
                            importKind === 'audio_bed' ? importLicenseRef || undefined : undefined,
                          filename: file?.name,
                          mime: file?.type || undefined,
                          base64,
                        },
                      },
                    })
                  ).then((result) => {
                    if (result?.audioBed?.id) {
                      setAudioMode('royalty_free_bed');
                      setBedAssetId(result.audioBed.id);
                      setBedLicenseRef(result.audioBed.licenseRef || importLicenseRef);
                      setStarterId('blank');
                      setSection('create');
                    }
                    if (result?.pack?.id) {
                      setSelectedId(result.pack.id);
                      setSection('review');
                    }
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  });
                }}
              >
                <label>
                  Kind
                  <select
                    value={importKind}
                    onChange={(e) => setImportKind(e.target.value)}
                  >
                    <option value="proof_still">Proof still (image)</option>
                    <option value="pack_template">Pack template (draft)</option>
                    <option value="master">Master video (mp4 → ready)</option>
                    <option value="audio_bed">Licensed RF audio bed</option>
                  </select>
                </label>
                {importKind === 'proof_still' || importKind === 'audio_bed' ? (
                  <>
                    <label>
                      {importKind === 'audio_bed' ? 'Bed id' : 'Proof id'}
                      <input
                        required
                        value={importId}
                        onChange={(e) => setImportId(e.target.value)}
                        placeholder={
                          importKind === 'audio_bed' ? 'yt-library-calm-pulse' : 'nw-cash-heading-alt'
                        }
                      />
                    </label>
                    <label>
                      Label
                      <input
                        value={importLabel}
                        onChange={(e) => setImportLabel(e.target.value)}
                        placeholder={importKind === 'audio_bed' ? 'Calm pulse (YAL)' : 'Cash heading alt'}
                      />
                    </label>
                    {importKind === 'audio_bed' ? (
                      <label>
                        License ref
                        <input
                          required
                          value={importLicenseRef}
                          onChange={(e) => setImportLicenseRef(e.target.value)}
                          placeholder="YouTube Audio Library — Track (https://…)"
                        />
                      </label>
                    ) : null}
                  </>
                ) : (
                  <>
                    <label>
                      Slug
                      <input
                        required
                        value={importSlug}
                        onChange={(e) => setImportSlug(e.target.value)}
                        placeholder="imported-cash-reel-01"
                      />
                    </label>
                    <label>
                      Caption
                      <textarea
                        rows={2}
                        value={importCaption}
                        onChange={(e) => setImportCaption(e.target.value)}
                      />
                    </label>
                    <label>
                      CTA
                      <input value={importCta} onChange={(e) => setImportCta(e.target.value)} />
                    </label>
                    <label>
                      Proof ids (comma-separated)
                      <input
                        value={importProofIds}
                        onChange={(e) => setImportProofIds(e.target.value)}
                        placeholder="cash-forecast-hero"
                        required={importKind === 'pack_template'}
                      />
                    </label>
                  </>
                )}
                {importKind === 'proof_still' || importKind === 'master' || importKind === 'audio_bed' ? (
                  <label>
                    File
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={
                        importKind === 'master'
                          ? 'video/mp4'
                          : importKind === 'audio_bed'
                            ? 'audio/*'
                            : 'image/*'
                      }
                      required
                    />
                  </label>
                ) : null}
                <button type="submit" className="primary-button" disabled={busy}>
                  {importing ? 'Importing…' : 'Import'}
                </button>
              </form>
            )}
          </section>
        </div>
      ) : null}
    </ModuleShell>
  );
}
